import type { GableRoofConvexVolume, GableRoofExtrusion } from "building-types/roofs/gableRoofExtrusionTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { RoofHeelHeightOffsets } from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import type { WallPlaneMasses } from "building-types/walls2/wallPlane";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import {
  faceOrNullCopy,
  faceOrNullMove,
  faceOrNullMoveAlongNormal,
  faceNew,
  faceOrNullReverse,
  faceCopy,
  faceMove,
} from "geom/brep";
import {
  addOutsidePolygonsToBSP,
  buildBSPNode,
  buildEdgePolygon,
  buildPolygonsFromFaces,
  buildPolygonsFromPolyline2,
  copyBSP,
  polygonBSPMove,
  polygonBSPMoveAlongNormal,
  polygonBSPNew,
  splitPolygonBSP,
  splitPolygonPlaneBSP,
} from "geom/bsp";
import { splitFaceBSP, splitFacesBSP } from "geom/bspClipping";
import type { MaxBSPDepthErrorHandler, NodeBSP, PolygonBSP } from "geom/bspTypes";
import { extrudeFace, extrusionToBRep } from "geom/extrude";
import { planeNewXY, planeReProjectPolyLine2, planeReverse, polyLine2OnPlane } from "geom/plane";
import { planeBSPByThreePoints, planeBSPFromPlane } from "geom/planeBSP";
import {
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2SetCW,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3EdgeCount, polyLine3EdgeEnd, polyLine3EdgeStart } from "geom/polyLine3";
import { getEaveDepth } from "../elements/roofTrimUtility";
import { updateInsideRoofBSPWithWallPlaneMasses } from "./wallPlanesRoofClipContext";

export const ROOF_CLIP_CONTEXT_TOP_OFFSET = -0.1;

function multiGableRoofClipContextNew(
  roofsBSP: NodeBSP | null,
  insideBSP: NodeBSP | null,
  discardBSP: NodeBSP | null,
  ridgeDiscardBSP: NodeBSP | null,
  discardNoDormersBSP: NodeBSP | null,
  discardNoShedOrDormersBSP: NodeBSP | null,
  overlapBSP: NodeBSP | null,
): MultiGableRoofClipContext {
  return {
    roofsBSP,
    insideBSP,
    discardBSP,
    ridgeDiscardBSP,
    discardNoDormersBSP,
    discardNoShedOrDormersBSP,
    overlapBSP,
  };
}

export function multiGableRoofClipContextByUpperFloorBSP(upperFloorBSP: NodeBSP | null): MultiGableRoofClipContext {
  return multiGableRoofClipContextNew(
    null,
    copyBSP(upperFloorBSP),
    copyBSP(upperFloorBSP),
    copyBSP(upperFloorBSP),
    copyBSP(upperFloorBSP),
    copyBSP(upperFloorBSP),
    null,
  );
}

interface UpdateClipContextOptions {
  clipContext: MultiGableRoofClipContext;
  roofSettings: RoofSettings;
  subRoofId: string;
  hasStartExtension: boolean;
  hasEndExtension: boolean;
  peakDirection: RoofPeakDirection | undefined;
  wallPerimeter: PolyLine2;
  offsetWallPerimeter: PolyLine2;
  srcPerimeter: PolyLine2;
  exactEdges: RoofEdge[];
  heelHeightOffsets: RoofHeelHeightOffsets;
  baseHeight: number;
  wallExtensionHeightLeft: number;
  wallExtensionHeightRight: number;
  roofTopFaces: RoofFace[];
  roofTopFacesWithOverlap: RoofFace[];
  unclippedRoofTopFaces: RoofFace[];
  extrusion: GableRoofExtrusion;
  startFrontFace?: Face;
  endFrontFace?: Face;
  startDiscardFrontFace?: Face;
  endDiscardFrontFace?: Face;
  shedRoofHalfFace: Face | null;
  shedRoofWallHalfFace: Face | null;
  clipEndFacesBSP?: NodeBSP;
  extraHipFaces?: RoofFace[];
  soffitDepth: number;
  isDormer: boolean;
  isShedRoof: boolean;
  isFloatingVirtualRoof: boolean;
  isStartTruncated: boolean;
  isEndTruncated: boolean;
  maxDepthErrorHandle: MaxBSPDepthErrorHandler;
  wallPlaneMasses?: WallPlaneMasses;
  peakHeight?: number;
}

interface UpdateClipContextResult {
  readonly clipBottomBSP: NodeBSP | null;
  readonly discardFriezeBSP: NodeBSP | null;
  readonly discardWallsBSP: NodeBSP | null;
  readonly discardStartWallBSP: NodeBSP | null;
  readonly discardEndWallBSP: NodeBSP | null;
  readonly roofTopBSP: NodeBSP | null;
}

export function updateClipContext({
  clipContext,
  roofSettings,
  subRoofId,
  hasStartExtension,
  hasEndExtension,
  peakDirection,
  wallPerimeter,
  offsetWallPerimeter,
  exactEdges,
  srcPerimeter,
  heelHeightOffsets,
  baseHeight,
  wallExtensionHeightLeft,
  wallExtensionHeightRight,
  roofTopFaces,
  roofTopFacesWithOverlap,
  unclippedRoofTopFaces,
  extrusion,
  startFrontFace,
  endFrontFace,
  startDiscardFrontFace,
  endDiscardFrontFace,
  shedRoofHalfFace,
  shedRoofWallHalfFace,
  clipEndFacesBSP,
  extraHipFaces,
  soffitDepth,
  isDormer,
  isShedRoof,
  isFloatingVirtualRoof,
  isStartTruncated,
  isEndTruncated,
  maxDepthErrorHandle,
  wallPlaneMasses,
  peakHeight,
}: UpdateClipContextOptions): UpdateClipContextResult {
  const {
    discardBSP,
    ridgeDiscardBSP,
    insideBSP,
    roofsBSP,
    discardNoDormersBSP,
    discardNoShedOrDormersBSP,
    overlapBSP,
  } = clipContext;

  let { refOffset: refHeelHeightOffset } = heelHeightOffsets;
  refHeelHeightOffset = Math.max(refHeelHeightOffset, 0);

  const wallExtensionHeight = Math.min(wallExtensionHeightLeft, wallExtensionHeightRight);

  // add the new clipping faces to the BSP
  clipContext.roofsBSP = updateRoofBSP({
    rootBSP: roofsBSP,
    subRoofId,
    hasStartExtension,
    hasEndExtension,
    perimeter: wallPerimeter,
    extrusion,
    convexVolumes: extrusion.convexVolumes,
    startFrontFace,
    endFrontFace,
    exactEdges,
    bottomOffset: baseHeight - getEaveDepth(roofSettings) - 1,
    height: wallExtensionHeight + getEaveDepth(roofSettings) + 1,
    shedRoofHalfFace: shedRoofWallHalfFace,
    clipEndFacesBSP,
    addBottom: true,
    maxDepthErrorHandle,
  });

  const addBottom = isFloatingVirtualRoof;
  const bottomOffset = addBottom
    ? baseHeight - getEaveDepth(roofSettings) - refHeelHeightOffset - wallExtensionHeight
    : 0;
  const height = addBottom
    ? wallExtensionHeight + getEaveDepth(roofSettings) + refHeelHeightOffset + 0.1
    : baseHeight + wallExtensionHeight + 0.1;
  const topOffset = ROOF_CLIP_CONTEXT_TOP_OFFSET;

  if (wallPlaneMasses) {
    clipContext.insideBSP = updateInsideRoofBSPWithWallPlaneMasses({
      rootBSP: insideBSP,
      subRoofId,
      roofTopFacesWithOverlap,
      exactEdges,
      topOffset,
      extraHipFaces,
      bottomOffset,
      addBottom,
      maxDepthErrorHandle,
      wallPlaneMasses,
      peakHeight: peakHeight ?? 0,
    });
  } else {
    clipContext.insideBSP = updateInsideRoofBSP({
      rootBSP: insideBSP,
      subRoofId,
      perimeter: wallPerimeter,
      roofTopFacesWithOverlap,
      startFrontFace,
      endFrontFace,
      exactEdges,
      topOffset,
      extraHipFaces,
      height,
      bottomOffset,
      addBottom,
      addExtrahipFaceEdges: true,
      maxDepthErrorHandle,
    });
  }

  if (clipContext.individualInsideBSPs !== undefined) {
    if (wallPlaneMasses) {
      clipContext.individualInsideBSPs[subRoofId] = updateInsideRoofBSPWithWallPlaneMasses({
        rootBSP: clipContext.individualInsideBSPs[subRoofId] ?? null,
        subRoofId,
        roofTopFacesWithOverlap,
        exactEdges,
        topOffset,
        extraHipFaces,
        bottomOffset,
        addBottom,
        maxDepthErrorHandle,
        wallPlaneMasses,
        peakHeight: peakHeight ?? 0,
      });
    } else {
      clipContext.individualInsideBSPs[subRoofId] = updateInsideRoofBSP({
        rootBSP: clipContext.individualInsideBSPs[subRoofId] ?? null,
        subRoofId,
        perimeter: wallPerimeter,
        roofTopFacesWithOverlap,
        startFrontFace,
        endFrontFace,
        exactEdges,
        topOffset,
        extraHipFaces,
        height,
        bottomOffset,
        addBottom,
        addExtrahipFaceEdges: true,
        maxDepthErrorHandle,
      });
    }
  }

  clipContext.discardBSP = updateInsideRoofBSP({
    rootBSP: discardBSP,
    subRoofId,
    perimeter: offsetWallPerimeter,
    roofTopFacesWithOverlap: unclippedRoofTopFaces,
    startFrontFace,
    endFrontFace,
    exactEdges,
    topOffset,
    extraHipFaces,
    height,
    bottomOffset,
    addBottom,
    maxDepthErrorHandle,
  });

  if (!isDormer && !isShedRoof) {
    clipContext.discardNoShedOrDormersBSP = updateInsideRoofBSP({
      rootBSP: discardNoShedOrDormersBSP,
      subRoofId,
      perimeter: offsetWallPerimeter,
      roofTopFacesWithOverlap: unclippedRoofTopFaces,
      startFrontFace,
      endFrontFace,
      exactEdges,
      topOffset,
      extraHipFaces,
      height,
      bottomOffset,
      addBottom,
      maxDepthErrorHandle,
    });
  }

  const ridgeBottomOffset = baseHeight - getEaveDepth(roofSettings);
  const ridgeHeight = wallExtensionHeight + getEaveDepth(roofSettings) + 0.1;
  clipContext.ridgeDiscardBSP = updateInsideRoofBSP({
    rootBSP: ridgeDiscardBSP,
    subRoofId,
    perimeter: offsetWallPerimeter,
    roofTopFacesWithOverlap: unclippedRoofTopFaces,
    startFrontFace,
    endFrontFace,
    exactEdges,
    topOffset,
    extraHipFaces,
    height: ridgeHeight,
    bottomOffset: ridgeBottomOffset,
    addBottom: true,
    maxDepthErrorHandle,
  });

  if (!isDormer) {
    clipContext.discardNoDormersBSP = updateRoofBSP({
      rootBSP: discardNoDormersBSP,
      subRoofId,
      hasStartExtension,
      hasEndExtension,
      perimeter: offsetWallPerimeter,
      extrusion,
      onlyTopFaces: true,
      startFrontFace: startDiscardFrontFace,
      endFrontFace: endDiscardFrontFace,
      exactEdges,
      height,
      topOffset,
      shedRoofHalfFace,
      clipEndFacesBSP,
      addCaps: peakDirection === RoofPeakDirection.HORIZONTAL,
      extraHipFaces,
      bottomOffset,
      addBottom,
      closeExtensions: true,
      maxDepthErrorHandle,
    });
  }

  clipContext.overlapBSP = updateRoofBSP({
    rootBSP: overlapBSP,
    subRoofId,
    hasStartExtension,
    hasEndExtension,
    roofTopFaces,
    height: baseHeight + wallExtensionHeight,
    shedRoofHalfFace,
    clipEndFacesBSP,
    addBottom: true,
    maxDepthErrorHandle,
  });

  const clipBottomPolygons: PolygonBSP[] = [];
  for (let i = 0, l = polyLine2EdgeCount(srcPerimeter); i < l; i++) {
    const [ax, ay] = polyLine2EdgeStart(srcPerimeter, i);
    const [bx, by] = polyLine2EdgeEnd(srcPerimeter, i);

    const edgePolygon = buildEdgePolygon(ax, ay, 0.0, bx, by, 0.0, 0.0, 0.0, baseHeight);
    if (!edgePolygon) {
      continue;
    }
    clipBottomPolygons.push(edgePolygon);
  }

  clipBottomPolygons.push(
    ...buildPolygonsFromPolyline2(polyLine2SetCW(polyLine2Copy(srcPerimeter)), planeNewXY(0, 0, baseHeight), 0),
  );

  const basePlane = planeReverse(planeNewXY(0, 0, baseHeight - getEaveDepth(roofSettings)));

  const discardFriezePolygons: PolygonBSP[] = [];
  const roofTopPolygons: PolygonBSP[] = [];
  for (let i = 0, l = roofTopFaces.length; i < l; i++) {
    const topFace = roofTopFaces[i].face;
    const { loops, plane } = topFace;

    if (loops.length === 0) {
      continue;
    }

    const pl3 = polyLine2OnPlane(plane, loops[0]);
    addEdgesPolygons(pl3, basePlane.originZ, discardFriezePolygons);

    buildPolygonsFromFaces({
      faces: [topFace],
      polygons: discardFriezePolygons,
    });

    buildPolygonsFromFaces({
      faces: [topFace],
      polygons: roofTopPolygons,
    });

    buildPolygonsFromFaces({
      faces: [faceNew([planeReProjectPolyLine2(loops[0], plane, basePlane)], basePlane)],
      polygons: discardFriezePolygons,
    });
  }

  const discardWallPolygons = buildPolygonsFromFaces({
    faces: (extraHipFaces ?? []).map((x) => x.face),
  });

  for (let i = 0, l = roofTopFacesWithOverlap.length; i < l; i++) {
    const topFace = roofTopFacesWithOverlap[i].face;

    buildPolygonsFromFaces({
      faces: [topFace],
      polygons: discardWallPolygons,
    });
  }

  const soffitOffsetZ = Math.min(-soffitDepth + 1, -MULTIGABLE_BSP_EPSILON);

  const discardSideWallPolygons = discardWallPolygons.map((x) => polygonBSPMove(x, 0, 0, soffitOffsetZ));

  const discardStartWallPolygons = discardWallPolygons.map((x) =>
    polygonBSPMove(x, 0, 0, isStartTruncated ? soffitOffsetZ : -MULTIGABLE_BSP_EPSILON),
  );

  const discardEndWallPolygons = discardWallPolygons.map((x) =>
    polygonBSPMove(x, 0, 0, isEndTruncated ? soffitOffsetZ : -MULTIGABLE_BSP_EPSILON),
  );

  return {
    clipBottomBSP: buildBSPNode({ polygons: clipBottomPolygons, maxDepthErrorHandle }),
    discardFriezeBSP: buildBSPNode({ polygons: discardFriezePolygons, maxDepthErrorHandle }),
    discardWallsBSP: buildBSPNode({ polygons: discardSideWallPolygons, maxDepthErrorHandle }),
    discardStartWallBSP: buildBSPNode({ polygons: discardStartWallPolygons, maxDepthErrorHandle }),
    discardEndWallBSP: buildBSPNode({ polygons: discardEndWallPolygons, maxDepthErrorHandle }),
    roofTopBSP: buildBSPNode({ polygons: roofTopPolygons, maxDepthErrorHandle }),
  };
}

function addEdgesPolygons(pl3: PolyLine3, bottom: number, polygons: PolygonBSP[]) {
  for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
    const x1 = pl3[i];
    const y1 = pl3[i + 1];
    const z1 = pl3[i + 2];

    const x2 = pl3[i + 3];
    const y2 = pl3[i + 4];
    const z2 = pl3[i + 5];

    const x3 = x2;
    const y3 = y2;
    const z3 = bottom;

    const x4 = x1;
    const y4 = y1;
    const z4 = bottom;

    const edgePlane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

    if (!edgePlane) {
      continue;
    }

    polygons.push(polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], edgePlane));
  }
}

interface UpdateRoofBSPOptions {
  readonly rootBSP: NodeBSP | null;
  readonly subRoofId: string;
  readonly hasStartExtension: boolean;
  readonly hasEndExtension: boolean;
  readonly perimeter?: PolyLine2 | null;
  readonly exactEdges?: RoofEdge[];
  readonly height: number;
  readonly roofTopFaces?: RoofFace[];
  readonly extrusion?: GableRoofExtrusion;
  readonly convexVolumes?: GableRoofConvexVolume[];
  readonly startFrontFace?: Face | null;
  readonly endFrontFace?: Face | null;
  readonly endEdgeOffset?: number;
  readonly invalidSideOffset?: number;
  readonly bottomOffset?: number;
  readonly topOffset?: number;
  readonly onlyTopFaces?: boolean;
  readonly shedRoofHalfFace: Face | null;
  readonly clipEndFacesBSP?: NodeBSP | null;
  readonly addBottom?: boolean;
  readonly addCaps?: boolean;
  readonly closeExtensions?: boolean;
  readonly extraHipFaces?: RoofFace[];
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
}

export function updateRoofBSP({
  rootBSP,
  subRoofId,
  hasStartExtension,
  hasEndExtension,
  perimeter = null,
  roofTopFaces,
  extrusion,
  convexVolumes,
  startFrontFace,
  endFrontFace,
  exactEdges,
  height,
  endEdgeOffset = 0.0,
  invalidSideOffset = 0.0,
  bottomOffset = 0.0,
  topOffset = 0.0,
  onlyTopFaces = false,
  shedRoofHalfFace = null,
  clipEndFacesBSP = null,
  addBottom = false,
  addCaps = false,
  closeExtensions = false,
  extraHipFaces = [],
  maxDepthErrorHandle,
}: UpdateRoofBSPOptions): NodeBSP | null {
  const polygons: PolygonBSP[] = [];
  const clipTopPolygons: PolygonBSP[] = [];
  const clipTopEdgePolygons: PolygonBSP[] = [];
  const edgePolygons: PolygonBSP[] = [];
  let shedRoofBSP: NodeBSP | null = null;

  const addPolygons = (
    polygonsToAdd: (PolygonBSP | null)[],
    polygonList: PolygonBSP[],
    clipWithHipPlanes: boolean = true,
  ) => {
    for (let i = 0, l = polygonsToAdd.length; i < l; i++) {
      let polygonToAdd = polygonsToAdd[i];

      if (clipWithHipPlanes) {
        for (const extraHipFace of extraHipFaces) {
          if (!polygonToAdd) {
            break;
          }

          const { backPolygon } = splitPolygonPlaneBSP(
            planeBSPFromPlane(extraHipFace.face.plane),
            polygonToAdd,
            MULTIGABLE_BSP_EPSILON,
          );
          polygonToAdd = backPolygon.vertices.length > 0 ? backPolygon : null;
        }
      }

      if (polygonToAdd) {
        polygonList.push(polygonToAdd);
      }
    }
  };

  const addFaces = (faces: Face[], polygonList: PolygonBSP[], clipWithHipPlanes: boolean = true) => {
    let polygonsToAdd: PolygonBSP[] = [];

    const srcPolygons = buildPolygonsFromFaces({ faces });

    if (shedRoofBSP) {
      for (let i = 0, l = srcPolygons.length; i < l; i++) {
        splitPolygonBSP({
          node: shedRoofBSP,
          polygon: srcPolygons[i],
          coplanarsToFront: true,
          backPolygons: polygonsToAdd,
          epsilon: MULTIGABLE_BSP_EPSILON,
        });
      }
    } else {
      polygonsToAdd = srcPolygons;
    }

    addPolygons(polygonsToAdd, polygonList, clipWithHipPlanes);
  };

  const addEdge = (pl: PolyLine2, edgeIndex: number, polygonList: PolygonBSP[], offset: number) => {
    let polygonsToAdd: PolygonBSP[] = [];

    const componentIndex = edgeIndex * 2;

    const ax = pl[componentIndex];
    const ay = pl[componentIndex + 1];
    const bx = pl[componentIndex + 2];
    const by = pl[componentIndex + 3];

    let edgePolygon = buildEdgePolygon(ax, ay, bottomOffset, bx, by, bottomOffset, 0.0, 0.0, height + topOffset);

    if (!edgePolygon) {
      return;
    }

    edgePolygon = polygonBSPMoveAlongNormal(edgePolygon, offset);

    if (shedRoofBSP) {
      splitPolygonBSP({
        node: shedRoofBSP,
        polygon: edgePolygon,
        coplanarsToFront: true,
        backPolygons: polygonsToAdd,
        epsilon: MULTIGABLE_BSP_EPSILON,
      });
    } else {
      polygonsToAdd = [edgePolygon];
    }

    addPolygons(polygonsToAdd, polygonList);
  };

  addFaces(
    extraHipFaces.map((x) => x.face),
    polygons,
    false,
  );

  if (shedRoofHalfFace && !roofTopFaces) {
    const shedRoofHalfPolygons = buildPolygonsFromFaces({
      faces: [shedRoofHalfFace],
    });

    shedRoofBSP = buildBSPNode({
      polygons: shedRoofHalfPolygons,
      epsilon: MULTIGABLE_BSP_EPSILON,
      maxDepthErrorHandle,
    });

    addPolygons(shedRoofHalfPolygons, clipTopPolygons);
    addPolygons(shedRoofHalfPolygons, polygons);
  }

  // build perimeter polygons
  if (perimeter) {
    const edgeCount = polyLine2EdgeCount(perimeter);
    const offsets: number[] = new Array<number>(edgeCount).fill(0);
    if (exactEdges) {
      for (let i = 0, l = edgeCount; i < l; i++) {
        const { isStartEdge, isEndEdge, side, fullyTruncated } = exactEdges[i];
        let offset = side === RoofSide.INVALID && !fullyTruncated ? invalidSideOffset : 0;
        offset = Math.max(offset, (isStartEdge || isEndEdge) && !fullyTruncated ? endEdgeOffset : 0);

        offsets[i] = offset;
      }
    }

    for (let i = 0, l = polyLine2EdgeCount(perimeter); i < l; i++) {
      addEdge(perimeter, i, clipTopEdgePolygons, offsets[i]);

      if (exactEdges) {
        const { isStartEdge, isEndEdge, fullyTruncated } = exactEdges[i];
        if (((isStartEdge && !startFrontFace) || (isEndEdge && !endFrontFace)) && !fullyTruncated) {
          continue;
        }
      }

      addEdge(perimeter, i, edgePolygons, offsets[i]);
      addEdge(perimeter, i, clipTopPolygons, offsets[i]);
    }
  }

  // add roofTops
  if (roofTopFaces) {
    for (let i = 0, l = roofTopFaces.length; i < l; i++) {
      const topPlate = extrudeFace(roofTopFaces[i].face, 0, 0, -GABLE_ROOF_THICKNESS);
      const brepRoofTop = extrusionToBRep(topPlate);

      const [bottomFace, topFace, ...sideFaces] = brepRoofTop.faces;

      const roofTopPlateFaces: Face[] = [];

      roofTopPlateFaces.push(topFace);

      if (!onlyTopFaces) {
        roofTopPlateFaces.push(...sideFaces);
        roofTopPlateFaces.push(bottomFace);
      }

      addFaces(roofTopPlateFaces, polygons);
    }
  }

  // add top faces from the three rail extrusion
  if (extrusion || convexVolumes) {
    const clipTopBSP = buildBSPNode({ polygons: clipTopPolygons, maxDepthErrorHandle });
    const clipTopEdgeBSP = buildBSPNode({ polygons: clipTopEdgePolygons, maxDepthErrorHandle });
    const extrusionPolygons: PolygonBSP[] = [];

    const addTopFaces = (topFaces: Face[]) => {
      for (let i = 0, l = topFaces.length; i < l; i++) {
        addTopFace(topFaces[i]);
      }
    };

    const addTopFace = (topFace: Face | null) => {
      if (topFace) {
        const offsetTopFace = faceMove(faceCopy(topFace), 0, 0, topOffset);

        const topFacesNoOverhang: Face[] = [];
        if (offsetTopFace) {
          if (clipTopBSP) {
            splitFaceBSP(offsetTopFace, {
              node: clipTopBSP,
              coplanarsToFront: true,
              backFaces: topFacesNoOverhang,
              epsilon: MULTIGABLE_BSP_EPSILON,
            });
          } else {
            topFacesNoOverhang.push(offsetTopFace);
          }
        }

        if (!perimeter) {
          const basePlane = planeReverse(planeNewXY(0, 0, 0));
          for (const { loops, plane } of topFacesNoOverhang) {
            if (loops.length === 0) {
              continue;
            }

            const pl3 = polyLine2OnPlane(plane, loops[0]);
            for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
              const x1 = pl3[i];
              const y1 = pl3[i + 1];
              const z1 = pl3[i + 2];

              const x2 = pl3[i + 3];
              const y2 = pl3[i + 4];
              const z2 = pl3[i + 5];

              const x3 = x2;
              const y3 = y2;
              const z3 = basePlane.originZ;

              const x4 = x1;
              const y4 = y1;
              const z4 = basePlane.originZ;

              const edgePlane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

              if (!edgePlane) {
                continue;
              }

              const edgePolygon = polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], edgePlane);
              addPolygons([edgePolygon], edgePolygons, true);
            }
          }
        }

        addFaces(topFacesNoOverhang, extrusionPolygons);

        if (closeExtensions && extraHipFaces.length === 0) {
          for (const { vertices } of extrusionPolygons) {
            for (let i = 0, l = vertices.length; i < l; i += 3) {
              const x1 = vertices[i];
              const y1 = vertices[i + 1];
              const z1 = vertices[i + 2];

              const x2 = vertices[(i + 3) % l];
              const y2 = vertices[(i + 4) % l];
              const z2 = vertices[(i + 5) % l];

              const x3 = x2;
              const y3 = y2;
              const z3 = 0;

              const x4 = x1;
              const y4 = y1;
              const z4 = 0;

              const edgePlane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

              if (!edgePlane) {
                continue;
              }

              const edgePolygon = polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], edgePlane);

              const tmpPolygons: PolygonBSP[] = [];
              splitPolygonBSP({
                node: clipTopEdgeBSP,
                polygon: edgePolygon,
                coplanarsToFront: false,
                frontPolygons: tmpPolygons,
                epsilon: MULTIGABLE_BSP_EPSILON,
              });

              addPolygons(tmpPolygons, edgePolygons);
            }
          }
        }
      }
    };

    const addCapFaces = (capFaces: Face[]) => {
      for (let i = 0, l = capFaces.length; i < l; i++) {
        addCapFace(capFaces[i]);
      }
    };

    const addCapFace = (capFace: Face) => {
      if (capFace) {
        const offsetCapFace = faceOrNullMove(faceOrNullCopy(capFace), 0, 0, topOffset);

        if (offsetCapFace) {
          const capFacesNoOverhang: Face[] = [];
          splitFaceBSP(offsetCapFace, {
            node: clipTopBSP,
            coplanarsToFront: true,
            backFaces: capFacesNoOverhang,
            epsilon: MULTIGABLE_BSP_EPSILON,
          });
          addFaces(capFacesNoOverhang, polygons);
        }
      }
    };

    if (extrusion) {
      if (addCaps) {
        if (!startFrontFace) {
          addCapFaces(extrusion.startFaces);
        }

        if (!endFrontFace) {
          addCapFaces(extrusion.endFaces);
        }
      }

      addTopFaces(extrusion.rightFaces);
      addTopFaces(extrusion.leftFaces);
      addTopFaces(extrusion.startHipFaces);
      addTopFaces(extrusion.endHipFaces);
    }

    if (convexVolumes) {
      for (const convexVolume of convexVolumes) {
        const { leftFace, rightFace, startFaces, endFaces, bottomFace, topFace } = convexVolume;

        addTopFace(leftFace);
        addTopFace(rightFace);

        if (!hasStartExtension) {
          addTopFaces(startFaces);
        }

        if (!hasEndExtension) {
          addTopFaces(endFaces);
        }

        addTopFace(bottomFace);

        if (topFace) {
          addTopFace(topFace);
        }
      }
    }

    const clipEdgesBSP = buildBSPNode({ polygons: extrusionPolygons, maxDepthErrorHandle });
    for (let i = 0, l = edgePolygons.length; i < l; ++i) {
      splitPolygonBSP({
        node: clipEdgesBSP,
        polygon: edgePolygons[i],
        coplanarsToFront: true,
        backPolygons: polygons,
        epsilon: MULTIGABLE_BSP_EPSILON,
      });
    }

    polygons.push(...extrusionPolygons);
  } else {
    polygons.push(...edgePolygons);
  }

  const addEndFace = (face: Face) => {
    const finalEndFace = faceOrNullCopy(face);
    faceOrNullMoveAlongNormal(finalEndFace, endEdgeOffset);
    faceOrNullMove(finalEndFace, 0, 0, topOffset);

    if (finalEndFace) {
      const frontFaces: Face[] = [];
      splitFaceBSP(finalEndFace, {
        node: clipEndFacesBSP,
        coplanarsToFront: true,
        frontFaces,
        epsilon: MULTIGABLE_BSP_EPSILON,
      });

      addFaces(frontFaces, polygons);
    }
  };

  // start/end faces
  if (startFrontFace) {
    addEndFace(startFrontFace);
  }

  if (endFrontFace) {
    addEndFace(endFrontFace);
  }

  // bottom
  if (addBottom && perimeter) {
    const bottomPlane = planeNewXY(0, 0, bottomOffset);
    const bottomLoop = polyLine2SetCW(polyLine2Copy(perimeter));
    const bottomFace = faceOrNullReverse(faceNew([bottomLoop], bottomPlane));
    if (bottomFace) {
      addFaces([bottomFace], polygons);
    }
  }

  // add the new clipping faces to the BSP
  return addOutsidePolygonsToBSP({
    node: rootBSP,
    polygons,
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
    userData: subRoofId,
  });
}

interface UpdateInsideRoofBSPOptions {
  readonly rootBSP: NodeBSP | null;
  readonly subRoofId: string;
  readonly perimeter?: PolyLine2;
  readonly exactEdges: RoofEdge[];
  readonly startFrontFace?: Face;
  readonly endFrontFace?: Face;
  readonly roofTopFacesWithOverlap: RoofFace[];
  readonly height: number;
  readonly endEdgeOffset?: number;
  readonly invalidSideOffset?: number;
  readonly bottomOffset?: number;
  readonly topOffset?: number;
  readonly addBottom?: boolean;
  readonly extraHipFaces?: RoofFace[];
  readonly addExtrahipFaceEdges?: boolean;
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
}

function updateInsideRoofBSP({
  rootBSP,
  subRoofId,
  perimeter,
  roofTopFacesWithOverlap,
  exactEdges,
  startFrontFace,
  endFrontFace,
  height,
  endEdgeOffset = 0.0,
  invalidSideOffset = 0.0,
  bottomOffset = 0.0,
  topOffset = 0.0,
  addBottom = false,
  addExtrahipFaceEdges = false,
  extraHipFaces = [],
  maxDepthErrorHandle,
}: UpdateInsideRoofBSPOptions): NodeBSP | null {
  const polygons: PolygonBSP[] = [];
  const clipTopPolygons: PolygonBSP[] = [];

  const extraHipPolygons = buildPolygonsFromFaces({
    faces: extraHipFaces.map((x) => x.face),
  });

  const extraHipFacesBSP = buildBSPNode({
    polygons: extraHipPolygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });

  const addPolygons = (polygonsToAdd: PolygonBSP[], polygonList: PolygonBSP[]) => {
    polygonList.push(...polygonsToAdd);
  };

  const addFaces = (faces: Face[], polygonList: PolygonBSP[]) => {
    const polygonsToAdd = buildPolygonsFromFaces({ faces });

    addPolygons(polygonsToAdd, polygonList);
  };

  const addEdge = (pl: PolyLine2, edgeIndex: number, polygonList: PolygonBSP[], offset: number) => {
    const componentIndex = edgeIndex * 2;

    const ax = pl[componentIndex];
    const ay = pl[componentIndex + 1];
    const bx = pl[componentIndex + 2];
    const by = pl[componentIndex + 3];

    let edgePolygon = buildEdgePolygon(ax, ay, bottomOffset, bx, by, bottomOffset, 0.0, 0.0, height + topOffset);

    if (!edgePolygon) {
      return;
    }

    edgePolygon = polygonBSPMoveAlongNormal(edgePolygon, offset);

    addPolygons([edgePolygon], polygonList);
  };

  // build perimeter polygons
  if (perimeter) {
    const edgeCount = polyLine2EdgeCount(perimeter);
    const offsets: number[] = new Array<number>(edgeCount).fill(0);
    if (exactEdges) {
      for (let i = 0, l = edgeCount; i < l; i++) {
        const { isStartEdge, isEndEdge, side, fullyTruncated } = exactEdges[i];
        let offset = side === RoofSide.INVALID && !fullyTruncated ? invalidSideOffset : 0;
        offset = Math.max(offset, (isStartEdge || isEndEdge) && !fullyTruncated ? endEdgeOffset : 0);

        offsets[i] = offset;
      }
    }

    for (let i = 0, l = polyLine2EdgeCount(perimeter); i < l; i++) {
      if (exactEdges) {
        const { isStartEdge, isEndEdge, fullyTruncated } = exactEdges[i];
        if (((isStartEdge && !startFrontFace) || (isEndEdge && !endFrontFace)) && !fullyTruncated) {
          continue;
        }
      }

      addEdge(perimeter, i, clipTopPolygons, offsets[i]);
    }
  }

  const clipTopBSP = buildBSPNode({
    polygons: clipTopPolygons,
    maxDepthErrorHandle,
  });

  // add top face edge polygons
  const addTopFacesEdges = (faces: Face[]) => {
    for (const { plane, loops } of faces) {
      if (!plane || !loops) {
        continue;
      }

      const pl3 = polyLine2OnPlane(plane, loops[0]);
      const pl3EdgeCount = pl3 ? polyLine3EdgeCount(pl3) : 0;
      for (let i = 0; i < pl3EdgeCount; i++) {
        const [x1, y1, z1] = polyLine3EdgeStart(pl3, i);
        const [x2, y2, z2] = polyLine3EdgeEnd(pl3, i);

        const x3 = x2;
        const y3 = y2;
        const z3 = bottomOffset;

        const x4 = x1;
        const y4 = y1;
        const z4 = bottomOffset;

        const edgePlane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

        if (!edgePlane) {
          return null;
        }

        const edgePolygon = polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], edgePlane);
        addPolygons([edgePolygon], polygons);
      }

      if (addBottom) {
        const bottomPlane = planeReverse(planeNewXY(0, 0, bottomOffset));
        const pl2 = planeReProjectPolyLine2(loops[0], plane, bottomPlane);
        const bottomFace = faceNew([pl2], bottomPlane);

        addFaces([bottomFace], polygons);
      }
    }
    return undefined;
  };

  // add top faces
  const addTopFace = (topFace: Face, isExtraHipFace: boolean = false) => {
    if (!topFace) {
      return;
    }

    const topFacesNoOverhang: Face[] = [];
    const offsetTopFace = faceOrNullMove(faceOrNullCopy(topFace), 0, 0, topOffset);
    if (offsetTopFace) {
      splitFaceBSP(offsetTopFace, {
        node: clipTopBSP,
        coplanarsToFront: true,
        backFaces: topFacesNoOverhang,
        epsilon: MULTIGABLE_BSP_EPSILON,
      });
    }

    const facesToAdd: Face[] = [];

    if (extraHipFacesBSP && !isExtraHipFace) {
      splitFacesBSP(topFacesNoOverhang, {
        node: extraHipFacesBSP,
        coplanarsToFront: false,
        backFaces: facesToAdd,
        epsilon: MULTIGABLE_BSP_EPSILON,
      });
    } else {
      facesToAdd.push(...topFacesNoOverhang);
    }

    if (!isExtraHipFace || addExtrahipFaceEdges) {
      addTopFacesEdges(facesToAdd);
    }

    addFaces(facesToAdd, polygons);
  };

  const addExtraHipFace = (face: Face) => {
    addTopFace(face, true);
  };

  const addExtraHipFaces = (roofFaces: RoofFace[]) => {
    for (const roofFace of roofFaces) {
      addExtraHipFace(roofFace.face);
    }
  };

  for (const topFace of roofTopFacesWithOverlap) {
    addTopFace(topFace.face);
  }

  addExtraHipFaces(extraHipFaces);

  // add the new clipping faces to the BSP
  return addOutsidePolygonsToBSP({
    node: rootBSP,
    polygons,
    coplanarsToFront: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
    userData: subRoofId,
  });
}
